import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  LightBulbIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { PageHero, SectionHeading } from '../components/page-templates/SitePageTemplates';

type ResourceCard = {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  paragraph?: string;
  listTitle?: string;
  list?: string[];
  link: string;
  cta: string;
};

const resourceCards: ResourceCard[] = [
  {
    icon: LightBulbIcon,
    title: 'Insights',
    description:
      'Industry perspectives and practical frameworks for cross-border logistics, ICT installation and regional coordination.',
    listTitle: 'Learn how to:',
    list: [
      'Reduce operational friction at scale',
      'Improve cross-border predictability',
      'Align warehousing and last-mile execution',
      'Scale without multiplying vendors',
    ],
    link: '/blog',
    cta: 'Explore Insights',
  },
  {
    icon: BriefcaseIcon,
    title: 'Case Studies',
    description: 'Execution in real operating environments.',
    paragraph:
      'See how TrueLog supports regional expansion, infrastructure projects and complex distribution models across multiple markets.',
    listTitle: 'Each case study outlines:',
    list: [
      'The operational challenge',
      'The coordination complexity',
      'The execution model',
      'The measurable outcome',
    ],
    link: '/case-studies',
    cta: 'View Case Studies',
  },
  {
    icon: QuestionMarkCircleIcon,
    title: 'FAQs',
    description: 'Clear answers before engagement.',
    paragraph:
      'From onboarding timelines to cross-border compliance and service scope, our FAQs address the practical questions organisations ask before committing.',
    link: '/faq',
    cta: 'Browse FAQs',
  },
];

const Resources: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Resources"
        title="Operational Insight. Proven Execution. Scale Without Friction."
        highlight="Scale Without Friction."
        description="TrueLog supports organisations operating across borders, teams and timelines. Our resources bring together practical insights, real-world case studies and clear guidance to help you plan, deliver and scale with confidence. Whether you are managing distribution, infrastructure installation, regional expansion or complex project rollouts, this is where execution becomes clearer."
        heroIcon={DocumentTextIcon}
        accentIcon={ShieldCheckIcon}
        actions={[
          { href: '/blog', label: 'Explore Insights' },
          { href: '/case-studies', label: 'View Case Studies', variant: 'secondary' },
        ]}
        badges={['Cross-border execution', 'Regional coordination', 'Practical guidance']}
        metrics={[
          { value: '3', label: 'Resource paths' },
          { value: '140+', label: 'Countries discussed in scope' },
          { value: '1', label: 'Integrated operating model' },
        ]}
        panelEyebrow="What you'll find"
        panelTitle="Resources shaped around execution reality"
        panelDescription="Designed for operators who need substance and clarity, not generic logistics content."
        panelItems={resourceCards.map(({ icon, title, description }) => ({ icon, title, description }))}
      />

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Browse"
            title="Explore Our Resources"
            description="Three paths, each built to help teams make better operating decisions faster."
          />

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {resourceCards.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.08 }}
                className="h-full"
              >
                <Link
                  to={item.link}
                  className="group flex h-full flex-col rounded-[30px] border border-slate-200/80 bg-white p-8 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.28)] transition-all duration-300 hover:-translate-y-1 hover:border-cyan-200 hover:shadow-[0_30px_70px_-35px_rgba(14,158,213,0.35)]"
                >
                  <div
                    className="inline-flex w-fit rounded-2xl p-3 text-white"
                    style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                  >
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold text-slate-950">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
                  {item.paragraph && (
                    <p className="mt-3 text-sm leading-7 text-slate-600">{item.paragraph}</p>
                  )}
                  {item.list && (
                    <div className="mt-5">
                      {item.listTitle && (
                        <p className="text-sm font-semibold text-slate-900">{item.listTitle}</p>
                      )}
                      <ul className="mt-3 space-y-2">
                        {item.list.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-2 text-sm leading-6 text-slate-600">
                            <CheckCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#0E9ED5]" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <span className="mt-auto inline-flex items-center gap-2 pt-8 text-sm font-semibold text-[#2447d8] transition-colors group-hover:text-[#0E9ED5]">
                    {item.cta}
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-950 py-24 text-white">
        <div
          className="absolute inset-0 opacity-90"
          style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.18),_transparent_55%)]" aria-hidden="true" />
        <div className="container-custom relative max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mx-auto max-w-4xl text-center"
          >
            <span className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-white/90">
              Closing
            </span>
            <h2 className="mt-6 text-4xl font-semibold leading-tight text-white sm:text-5xl">
              When Execution Needs to Work
            </h2>
            <div className="mt-8 space-y-5 text-base leading-8 sm:text-lg">
              <p className="text-white/95">Scaling across regions should not require multiple partners and constant follow-up.</p>
              <p className="text-white/95">
                TrueLog acts as a single point of operational ownership across warehousing, cross-border
                movement and last-mile fulfilment, enabling organisations to grow without adding execution
                friction.
              </p>
              <p className="text-white/95">If you are evaluating how to simplify and scale your regional operations, start here.</p>
            </div>
            <div className="mt-10 flex justify-center">
              <Link
                to="/contact-us"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-slate-950 shadow-[0_20px_45px_-20px_rgba(15,23,42,0.6)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_25px_55px_-18px_rgba(15,23,42,0.7)]"
              >
                Speak with Our Team
                <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
